import PermissionsClaimsOverrideManager from "@src/auth/permissions/PermissionsClaimsOverrideManager";
import { FeatureAccessContextProvider } from "@src/auth/permissions/FeatureAccessContext";
import {
  DataUseAndPermissionModal,
  useHasOutstandingTerms,
} from "@src/backpack-console/components/DataUseAndPermissionsModal/DataUseAndPermissionsModal";
import { useCurrentUser } from "@src/context/UserContext";
import IdentifyUserManager from "@src/IdentifyUserManager";
import { DownloadNotifier } from "@src/straps/derived/Notifier/DownloadNotifier";
import * as React from "react";
import { Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import AuthDesignRefreshGate from "@src/auth/AuthDesignRefreshGate";
import { Loader } from "../global_components";
import { AppRoute } from "../global_components/AppRoute";
import UnitsContextProvider from "../UnitsContextProvider";
import { BackpackLeftNav } from "./components/BackpackLeftNav";
import ConsoleNav from "./ConsoleNav";
import { CurrentAccountManager } from "./CurrentAccountManager";
import PageNotFound from "./PageNotFound";
import { SettingsRouter } from "./pages/Settings/SettingsRouter";

const Home = React.lazy(() => import("./pages/Homepage/Homepage"));
const Marketplace = React.lazy(() => import("./pages/Marketplace/Marketplace"));
const SiteDashboard = React.lazy(() => import("./SiteRouter"));
const BackpacksRollup = React.lazy(() => import("./pages/BackpacksRollup"));
const Print = React.lazy(() => import("./pages/Print"));
const ArcPack = React.lazy(() => import("./pages/ArcPack"));
const Rewards = React.lazy(() => import("../rewards/Router"));
const AuthActions = React.lazy(() => import("@src/auth/AuthActions"));
const AcceptInvite = React.lazy(() => import("@src/auth/AcceptInvite"));
const RefreshedAuthActions = React.lazy(
  () => import("@src/auth/refresh/AuthActions")
);
const RefreshedAcceptInvite = React.lazy(
  () => import("@src/auth/refresh/AcceptInvite")
);

// Wraps content with top and left nav
const BackpackPageWrapper: React.FunctionComponent<React.PropsWithChildren> = ({
  children,
}) => {
  const { isLoading } = useCurrentUser();

  return (
    <div className="app-wrapper">
      <ConsoleNav />
      <div className="flex grow overflow-x-auto">
        <div
          id="dropdown-root"
          className="flex min-w-[1280px] grow overflow-hidden"
        >
          <React.Suspense fallback={<Loader size={80} />}>
            <BackpackLeftNav />
          </React.Suspense>
          <main
            className="content-scroll-container isolate grow overflow-y-auto bg-straps-body py-5 pb-24 pl-[30px] pr-[20px]"
            style={{ scrollbarGutter: "stable" }}
          >
            {/* If currentUser exists then we can render main apps */}
            {!isLoading ? (
              <React.Suspense fallback={<Loader size={80} />}>
                {children}
              </React.Suspense>
            ) : (
              <Loader size={80} />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

function PageRouter() {
  const hasOutstandingTerms = useHasOutstandingTerms();

  if (hasOutstandingTerms) {
    return <DataUseAndPermissionModal />;
  }

  return (
    <BackpackPageWrapper>
      <Suspense fallback={<Loader />}>
        <Switch>
          <AppRoute path="/site/:siteId" render={() => <SiteDashboard />} />
          <AppRoute
            path={[
              "/rollup",
              "/reports",
              "/onboarding-completion",
              "/environmental-impact",
              "/equipment",
              "/projects",
              "/certifications",
              "/rebates",
            ]}
            render={() => <BackpacksRollup />}
          />
          <AppRoute
            path="/marketplace"
            exact
            render={() => (
              // Marketplace reads the Projects reward-vendors endpoint, so the
              // "Projects" feature must be checked for the access-check logging.
              <FeatureAccessContextProvider features={["Projects"]}>
                <Marketplace />
              </FeatureAccessContextProvider>
            )}
          />
          <AppRoute path="/" exact render={() => <Home />} />
          <AppRoute path="/settings" render={() => <SettingsRouter />} />
          <Redirect path="/admin" to="/settings/users" />
          <Redirect path="/settings" to="/settings/profile" />
          <Redirect path="/accounts" to="/settings/accounts" />
          <AppRoute render={() => <PageNotFound />} />
        </Switch>
      </Suspense>
    </BackpackPageWrapper>
  );
}

export default function BackpackConsoleRouter() {
  return (
    <>
      <PermissionsClaimsOverrideManager />
      <CurrentAccountManager />
      <DownloadNotifier />
      <IdentifyUserManager />
      <UnitsContextProvider fallback={<Loader />}>
        <Suspense fallback={<Loader />}>
          <Switch>
            {/* These pages are special because they don't have a page wrapper or nav components */}
            <AppRoute path="/print" render={() => <Print />} />
            <AppRoute path="/arcpack" render={() => <ArcPack />} />
            <AppRoute path="/rewards" render={() => <Rewards />} />
            <AppRoute
              path="/auth/action"
              render={() => (
                <AuthDesignRefreshGate
                  refreshed={<RefreshedAuthActions />}
                  legacy={<AuthActions />}
                />
              )}
            />
            <AppRoute
              path="/accept-invite"
              render={() => (
                <AuthDesignRefreshGate
                  refreshed={<RefreshedAcceptInvite />}
                  legacy={<AcceptInvite />}
                />
              )}
            />
            <Route render={() => <PageRouter />} />
          </Switch>
        </Suspense>
      </UnitsContextProvider>
    </>
  );
}
